import {
    backendServices,
    BillingMethod,
    BillingPlan,
    BusinessDocType,
    CompanyContactType,
    CreditStatus,
    CustomerDeliverySetting,
    CustomerPaymentType,
    DDOfferAcceptanceMode,
    DeliveryMethod,
    DocumentHandling,
    DocumentReleaseSchedule,
    FileType,
    GroupingMethod,
    GroupReleaseSchedule,
    InvoiceConfirmationSchedule,
    OnboardingStatus,
    PostEnvelopeSize,
    PostFormat,
    PurchaseOrderFileFormat,
    RecursivePartial,
    RegistrationStatus,
    RemittanceAdviceSchedule,
    RemittanceAdviceType,
    RuleStatus,
    StandardRoleType,
    SupplierCompanyOsnStatus,
} from 'common';
import { SupplierSignUpData } from './SupplierSignUpSchema';

function mapPODeliveryFileFormatToFileType(fileFormat: PurchaseOrderFileFormat | undefined): FileType {
    switch (fileFormat) {
        case PurchaseOrderFileFormat.PDF:
            return FileType.PDF;
        default:
            return FileType.XML;
    }
}

export class SupplierSignUpService {
    private api = new backendServices.Apis.CompanyApi();

    private readonly defaultRoleTypes = [
        StandardRoleType.SuperUserRights,
        StandardRoleType.CompanyAdminRights,
        StandardRoleType.SupplierAdminRights,
        StandardRoleType.InvoiceEditRights,
        StandardRoleType.SupplierLoginRights,
        StandardRoleType.BuyerAdminRights,
        StandardRoleType.BuyerLoginRights,
        StandardRoleType.ParkingLotAdminRights,
        StandardRoleType.SupplierCreationRights,
        StandardRoleType.SuperUserApprover,
    ];

    constructor(
        private readonly profileExtendedData: backendServices.ViewModels.ProfileViewModel['ProfileExtendedDatas'],
        private readonly buyerCompany: Pick<
            backendServices.ViewModels.CompanyViewModel,
            'Attributes' | 'OsnRequired' | 'ID'
        >,
        private readonly roleTypes: backendServices.ViewModels.RoleTypeViewModel[]
    ) {}

    private getDefaultRoleTypes() {
        return this.roleTypes.filter(({ Name }) => this.defaultRoleTypes.includes(Name as StandardRoleType));
    }

    private getAttributeFromExtendedDataList(attribute: string) {
        return this.profileExtendedData.find((data) => data.Name === attribute)?.Value ?? '';
    }

    private getProfileList(
        { customer: customerStep, business: businessStep }: SupplierSignUpData,
        contact: Partial<backendServices.ViewModels.ContactViewModel>
    ) {
        const currency = customerStep.invoiceCurrency;

        const isPoDeliveryEnabled = !!customerStep.poDelivery;
        const poDeliveryMethod = customerStep.poDelivery?.deliveryMethod;
        const poDeliveryFileFormat = customerStep.poDelivery?.deliveryFileFormat;

        const vatInformation = customerStep.vatInformation;

        const salesProfile = {
            ProfileType: backendServices.ViewModels.ProfileType.Sales,
            Name: businessStep.businessName,
            InvoiceConfirmationEmail: contact.Email,
            RemittanceAdviceEmail: contact.Email,
            Currency: currency,
            Address1: businessStep.address1,
            Address2: businessStep.address2,
            Address3: businessStep.address3,
            City: businessStep.city,
            State: businessStep.state,
            PostalCode: businessStep.postalCode,
            RemitToAddressBlock: null,
            Country: businessStep.country,
            PODeliveryMethodStatus: poDeliveryMethod,
            Phone: contact.Phone,
            Fax: businessStep.fax,
            Email: contact.Email,
            RuleStatus: RuleStatus.Sample,
            ProfileDeliveryMethods: isPoDeliveryEnabled
                ? [
                      {
                          Name: '',
                          AddressLine1: '',
                          AddressLine2: '',
                          AddressLine3: '',
                          City: '',
                          PostalCode: '',
                          Country: '',
                          State: '',
                          FaxNumber: '',
                          EmailAddress: customerStep.poDelivery?.deliveryEmail ?? '',
                          ProcessingMode: 1,
                          IsEnabled: true,
                          DeliveryMethod: poDeliveryMethod,
                          WhenUsed: 1,
                          GroupBy: 1,
                          GroupReleaseSchedule: 1,
                          BusinessDocType: BusinessDocType.PurchaseOrder,
                          ProfileDeliveryFileTypes: [
                              {
                                  GroupingMethod: 2,
                                  IsEnabled: true,
                                  FileType: mapPODeliveryFileFormatToFileType(poDeliveryFileFormat),
                              },
                          ],
                      },
                  ]
                : [],
        };

        const supplierWarehouseProfile = {
            ProfileType: backendServices.ViewModels.ProfileType.SupplierWarehouse,
            Name: businessStep.businessName,
            InvoiceConfirmationEmail: contact.Email,
            RemittanceAdviceEmail: contact.Email,
            Currency: currency,
            Address1: businessStep.address1,
            Address2: businessStep.address2,
            Address3: businessStep.address3,
            City: businessStep.city,
            State: businessStep.state,
            PostalCode: businessStep.postalCode,
            Country: businessStep.country,
            PODeliveryMethodStatus: DeliveryMethod.Email,
            Phone: contact.Phone,
            Fax: businessStep.fax,
            Email: contact.Email,
            RuleStatus: RuleStatus.Sample,
        };

        const invoiceProfile = {
            ProfileType: backendServices.ViewModels.ProfileType.Invoice,
            Name: businessStep.businessName,
            InvoiceConfirmationEmail: contact.Email,
            RemittanceAdviceEmail: contact.Email,
            Currency: currency,
            Address1: businessStep.address1,
            Address2: businessStep.address2,
            Address3: businessStep.address3,
            City: businessStep.city,
            State: businessStep.state,
            PostalCode: businessStep.postalCode,
            Country: businessStep.country,
            PODeliveryMethodStatus: DeliveryMethod.Email,
            Phone: contact.Phone,
            Fax: businessStep.fax,
            Email: contact.Email,
            RuleStatus: RuleStatus.Sample,
            SignVATInvoices: vatInformation?.SignVATInvoices ?? null,
            VATComplianceRequired: vatInformation?.VATComplianceRequired ?? false,
            VATNumber: vatInformation?.VATNumber ?? null,
        };

        return [salesProfile, supplierWarehouseProfile, invoiceProfile];
    }

    async createSupplier(
        formData: SupplierSignUpData,
        submissionMethodType: backendServices.ViewModels.SubmissionMethod
    ) {
        const { business: businessStep, accountAndContact: accountAndContactStep } = formData;

        // Combine first and last name for the API
        const fullName = `${accountAndContactStep.firstName} ${accountAndContactStep.lastName}`.trim();

        const companyContact: Partial<backendServices.ViewModels.ContactViewModel> = {
            Name: fullName,
            Title: accountAndContactStep.title,
            Phone: accountAndContactStep.phone,

            Email: accountAndContactStep.loginEmail,
            Address1: businessStep.address1,
            Address2: businessStep.address2,
            Address3: businessStep.address3,
            City: businessStep.city,
            State: businessStep.state,
            Fax: businessStep.fax,
            PostalCode: businessStep.postalCode,
            Country: businessStep.country,
            Type: CompanyContactType.Support,
        };

        const profile: RecursivePartial<backendServices.ViewModels.CompanyUserProfileListViewModel> = {
            CompanyUserProfiles: [
                {
                    VendorsCustomerNumber: '',
                    VendorClass: '',
                    VendorNumber: '',
                    VendorOrgUnit: '',
                    VendorSiteId: '',
                    PurchaseOrderDeliveryMethodFileFormat: formData.customer.poDelivery?.deliveryFileFormat,
                    Company: {
                        IsSupplier: true,
                        Name: businessStep.businessName,
                        Email: accountAndContactStep.loginEmail,
                        Phone: businessStep.phone,
                        CreditStatus: CreditStatus.Active,
                        Address1: businessStep.address1,
                        Address2: businessStep.address2,
                        Address3: businessStep.address3,
                        City: businessStep.city,
                        PostalCode: businessStep.postalCode,
                        State: businessStep.state ?? '', // This is optional in the schema
                        Country: businessStep.country,
                        DDOfferAcceptanceMode: DDOfferAcceptanceMode.Normal,
                        OnboardingStatus: OnboardingStatus.Pending,
                        DefaultLanguage: 'en-US',
                        AllowInvoiceWebEntry: true,
                        BillingMethod: BillingMethod.Transcepta,
                        BillingPlan: BillingPlan.PerTransaction,
                        DocumentReleaseSchedule: DocumentReleaseSchedule.ReleaseNow,
                        DocumentReleaseTime: new Date(1900, 1, 1, 20, 0, 0).toJSON(),
                        InvoiceConfirmationSchedule: InvoiceConfirmationSchedule.Individual,
                        RemittanceAdviceSchedule: RemittanceAdviceSchedule.Individual,
                        SupplierDefaultPaymentType: CustomerPaymentType.All,
                        DefaultPostFormat: PostFormat.BlackAndWhite,
                        AllowMarketingMaterialDefault: false,
                        SupplierDefaultEmailGroupingEnabled: true,
                        SupplierDefaultEmailGroupingMethod: GroupingMethod.Combined,
                        SupplierDefaultElectronicGroupingEnabled: false,
                        ReleaseTime1: new Date(1900, 1, 1, 20, 0, 0).toJSON(),
                        ReleaseTime2: null,
                        SupplierDefaultGroupReleaseSchedule: GroupReleaseSchedule.DocumentBatch,
                        SupplierFaxGroupingEnabled: true,
                        // Only legacy vendors need universal send.  Just about all new vendors will be Buyer Only
                        HasUniversalSend: false,
                        DefaultInvoiceDeliverySetting: CustomerDeliverySetting.UseBuyer,
                        SupplierPostGroupingEnabled: true,
                        PostCoverPage: true,
                        PostDuplexPrinting: false,
                        PostEnvelope: PostEnvelopeSize.Env6x9,
                        SupplierMaxEmailSize: 1024, // Defaults to 1mb during registration
                        RemittanceAdviceType: RemittanceAdviceType.RemittanceAdvice,
                        DefaultInvoiceDeliveryMethod: DeliveryMethod.Email,
                        RegistrationStatus: RegistrationStatus.Completed,
                        SupplierIsScrapingEmail: false,
                        SupplierIsScrapingFax: false,
                        SupplierIsScrapingPost: false,
                        BillingPeriodStartDay: 1,
                        BillingStartDate: null,
                        SupplierDocumentBatchConfirmations: true,
                        InvoiceReleaseScheduled: true,
                        Fax: businessStep.fax,
                        TIN: '',
                        OsnStatus:
                            this.buyerCompany.OsnRequired === true
                                ? SupplierCompanyOsnStatus.Pending
                                : SupplierCompanyOsnStatus.NotRequired,
                        MiscInfoXML: `<root><RegistrationType>Instant</RegistrationType><RegistrationCampaign>Default</RegistrationCampaign></root>`,
                        InitialActivationTime: new Date().toJSON(),
                        SupportWatch: 1,
                        NumberOfDaysToArchive: 60,
                        DefaultInvoiceSubmissionMethod: submissionMethodType,
                        DocumentHandling: DocumentHandling.Normal,
                        PurchaseOrderAcknowledgementReleaseScheduled: false,
                        ASNReleaseScheduled: false,
                        FunctionalAcknowledgementReleaseScheduled: false,
                        PurchaseOrderReleaseScheduled: false,
                        InventoryAdviceReleaseScheduled: false,
                        ReceivingAdviceReleaseScheduled: false,
                        ConsumptionAdviceReleaseScheduled: false,
                        DUNSNumber: businessStep.dunsNumber ?? '',
                        Contacts: [companyContact],
                    },
                    User: {
                        Email: accountAndContactStep.loginEmail,
                        Password: accountAndContactStep.password,
                        Phone: accountAndContactStep.phone,
                        Name: fullName,
                        ShowInvoiceSubmittedPopupInSupplierPortal: true,
                        SubstituteUserActive: false,
                        UserRoles: this.getDefaultRoleTypes().map((rt) => ({ RoleID: rt.ID })),
                        // TODO: Add UserName?
                    },
                    Profiles: this.getProfileList(formData, companyContact),
                },
            ],
            CommaSeparatedDocumentTypes: 'INV',
            BuyersNameInSalesForce: this.getAttributeFromExtendedDataList('BuyersNameInSalesForce'),
            CampaignName: this.getAttributeFromExtendedDataList('CampaignName'),
            CampaignPhase: this.getAttributeFromExtendedDataList('CampaignPhaseForVendorFeed'),
            SalesforceGroupName: this.getAttributeFromExtendedDataList('SalesforceGroupName'),
            BuyerCompanyId: this.buyerCompany.ID,
            SendWelcomeEmail: true,
            SendNagMessages: true,
            SendServiceAgreementEmail: true,
        };

        const isSupplierRegistration =
            !!profile.BuyersNameInSalesForce &&
            !!profile.CampaignName &&
            !!profile.CampaignPhase &&
            !!profile.SalesforceGroupName;

        const res = await this.api.postCompanyUserProfile({ isSupplierRegistration: isSupplierRegistration }, profile);

        return res.data;
    }
}
